import {
  ReactNode,
  useContext,
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
  Children,
  isValidElement,
  FocusEvent,
  RefObject,
} from 'react';
import { useLocation, resolvePath } from 'react-router-dom';
import { useSidebarOpenState } from '@backstage/core-components';
import {
  deferFocus,
  FOCUSABLE_SELECTOR,
} from '@internal/backstage-plugin-neo4j-ui-kit-react';
import { SidebarContext } from '../SidebarContext';

/**
 * Check if a path matches the current location
 */
const isLocationMatch = (
  currentPathname: string,
  targetPathname: string,
): boolean => {
  // Normalize paths (remove leading/trailing slashes for comparison)
  const normalize = (p: string) => p.replace(/^\/|\/$/g, '').toLowerCase();
  const current = normalize(currentPathname);
  const target = normalize(targetPathname);

  // Exact match or starts with target path followed by /
  return current === target || current.startsWith(`${target}/`);
};

/**
 * Recursively extract all 'to' props from children, including nested ones
 * (handles wrappers like RequirePermission, RequireFlag, etc.)
 */
const extractChildPaths = (children: ReactNode): string[] => {
  const paths: string[] = [];

  Children.forEach(children, child => {
    if (!isValidElement(child)) return;

    // If this child has a 'to' prop, add its resolved path
    if (child.props?.to) {
      const resolved = resolvePath(child.props.to);
      paths.push(resolved.pathname);
    }

    // Recursively check nested children
    if (child.props?.children) {
      paths.push(...extractChildPaths(child.props.children));
    }
  });

  return paths;
};

export type UseExpandableSidebarGroupStateProps = {
  children: ReactNode;
  id?: string;
  label: ReactNode;
};

export type UseExpandableSidebarGroupStateReturn = {
  groupId: string;
  isCollapsed: boolean;
  isExpanded: boolean;
  hasActiveChild: boolean;
  isHovered: boolean;
  isMainItemHovered: boolean;
  isSubmenuActive: boolean;
  onGroupHover: (groupId: string | null) => void;
  handleToggle: () => void;
  handleMainItemMouseEnter: () => void;
  handleMainItemMouseLeave: () => void;
  handlePopoutMouseEnter: () => void;
  handlePopoutMouseLeave: () => void;
  handleMainItemFocus: () => void;
  handleMainItemBlur: (e: FocusEvent) => void;
  handleActivateSubmenu: () => void;
  handleDeactivateSubmenu: () => void;
  onGroupFocus: (groupId: string | null) => void;
  mainItemRef: RefObject<HTMLElement>;
  popoutRef: RefObject<HTMLDivElement>;
};

export const useExpandableSidebarGroupState = ({
  children,
  id,
  label,
}: UseExpandableSidebarGroupStateProps): UseExpandableSidebarGroupStateReturn => {
  const {
    expandedGroups,
    onGroupToggle,
    hoveredGroupId,
    onGroupHover,
    onGroupFocus,
    activeSubmenuGroupId,
    onSubmenuActivate,
  } = useContext(SidebarContext);
  const { isOpen: isSidebarOpen } = useSidebarOpenState();
  const location = useLocation();

  // Refs for keyboard navigation
  const mainItemRef = useRef<HTMLElement>(null);
  const popoutRef = useRef<HTMLDivElement>(null);

  // Use id or convert label to string for group identification
  const groupId = id || (typeof label === 'string' ? label : 'unknown');

  // Sidebar is collapsed when it's not open
  const isCollapsed = !isSidebarOpen;

  // Derive hover state from shared context (instant switch between groups)
  const isHovered = hoveredGroupId === groupId;

  // Derive submenu active state
  const isSubmenuActive = activeSubmenuGroupId === groupId;

  // Extract child paths from children to check if any child route is active
  // Uses recursive extraction to handle wrappers like RequirePermission
  const childPaths = useMemo(() => extractChildPaths(children), [children]);

  // Check if any child route is currently active
  const hasActiveChild = useMemo(() => {
    return childPaths.some(path => isLocationMatch(location.pathname, path));
  }, [childPaths, location.pathname]);

  // Track previous sidebar state to detect transitions
  const prevSidebarOpenRef = useRef<boolean | null>(null);

  // Auto-expand when:
  // 1. On initial mount (page load/reload) with active child and sidebar expanded
  // 2. When sidebar transitions from collapsed to expanded and has active child
  useEffect(() => {
    const wasCollapsed = prevSidebarOpenRef.current === false;
    const isNowExpanded = isSidebarOpen;
    const isFirstRender = prevSidebarOpenRef.current === null;

    const shouldAutoExpand =
      hasActiveChild && isNowExpanded && (isFirstRender || wasCollapsed);

    if (shouldAutoExpand && !expandedGroups?.[groupId]) {
      onGroupToggle?.(groupId, true);
    }

    prevSidebarOpenRef.current = isSidebarOpen;
  }, [isSidebarOpen, hasActiveChild, expandedGroups, groupId, onGroupToggle]);

  const isExpanded = expandedGroups?.[groupId] ?? false;

  // Local state for immediate hover effect on main item only
  const [isMainItemHovered, setIsMainItemHovered] = useState(false);

  const handleToggle = useCallback(() => {
    onGroupToggle?.(groupId, !isExpanded);
  }, [onGroupToggle, groupId, isExpanded]);

  // Handlers for the main item - controls both visual hover (immediate) and popout (via context)
  const handleMainItemMouseEnter = useCallback(() => {
    setIsMainItemHovered(true);
    onGroupHover(groupId);
  }, [onGroupHover, groupId]);

  const handleMainItemMouseLeave = useCallback(() => {
    setIsMainItemHovered(false);
    onGroupHover(null);
  }, [onGroupHover]);

  // Handlers for popout/bridge - only controls popout visibility, not visual hover on main item
  const handlePopoutMouseEnter = useCallback(() => {
    onGroupHover(groupId);
  }, [onGroupHover, groupId]);

  const handlePopoutMouseLeave = useCallback(() => {
    onGroupHover(null);
  }, [onGroupHover]);

  // Focus handlers for keyboard navigation
  const handleMainItemFocus = useCallback(() => {
    onGroupFocus(groupId);
  }, [onGroupFocus, groupId]);

  const handleMainItemBlur = useCallback(
    (e: FocusEvent) => {
      // Only clear focus if focus is leaving the group entirely (not moving to popout)
      const relatedTarget = e.relatedTarget as Node | null;
      if (
        !popoutRef.current?.contains(relatedTarget) &&
        !mainItemRef.current?.contains(relatedTarget)
      ) {
        onGroupFocus(null);
      }
    },
    [onGroupFocus],
  );

  const handleActivateSubmenu = useCallback(() => {
    onSubmenuActivate(groupId);
    // Focus first focusable element in popout after render
    setTimeout(() => {
      const firstFocusable =
        popoutRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      deferFocus(firstFocusable);
    }, 0);
  }, [onSubmenuActivate, groupId]);

  const handleDeactivateSubmenu = useCallback(() => {
    onSubmenuActivate(null);
    // Return focus to main item
    deferFocus(mainItemRef.current);
  }, [onSubmenuActivate]);

  return {
    groupId,
    isCollapsed,
    isExpanded,
    hasActiveChild,
    isHovered,
    isMainItemHovered,
    isSubmenuActive,
    onGroupHover,
    handleToggle,
    handleMainItemMouseEnter,
    handleMainItemMouseLeave,
    handlePopoutMouseEnter,
    handlePopoutMouseLeave,
    handleMainItemFocus,
    handleMainItemBlur,
    handleActivateSubmenu,
    handleDeactivateSubmenu,
    onGroupFocus,
    mainItemRef,
    popoutRef,
  };
};
